/**
 * Procedural Cover Image Generator for Bytebeat
 *
 * Generates a vibrant, unique canvas image based on the bytebeat code and name.
 * Uses a seeded deterministic approach so the same code always gives the same image.
 */

import { compileFn } from './bytebeatCompiler';

function seededRng(seed: number): () => number {
  let s = seed;
  return function () {
    s = (Math.imul(1664525, s) + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function hashCode(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (Math.imul(h, 0x01000193)) >>> 0;
  }
  return h >>> 0;
}

function hslStr(h: number, s: number, l: number): string {
  return `hsl(${Math.round(h)},${Math.round(s)}%,${Math.round(l)}%)`;
}

export function generateCoverDataUrl(
  code: string,
  name: string,
  width = 400,
  height = 400,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  const seed = hashCode(code + '|' + name);
  const rng = seededRng(seed);

  // Base hue from seed
  const baseHue = rng() * 360;
  const hue2 = (baseHue + 120 + rng() * 60) % 360;
  const hue3 = (baseHue + 240 + rng() * 60) % 360;

  // Background gradient
  const grad = ctx.createLinearGradient(0, 0, width, height);
  grad.addColorStop(0, hslStr(baseHue, 70 + rng() * 25, 12));
  grad.addColorStop(0.5, hslStr(hue2, 65 + rng() * 25, 16));
  grad.addColorStop(1, hslStr(hue3, 70 + rng() * 20, 10));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);

  // Use the bytebeat code to drive waveform samples
  const waveLen = 256;
  const samples: number[] = [];
  try {
    const fn = compileFn(code, []);
    for (let i = 0; i < waveLen; i++) {
      try {
        const val = fn ? fn(i * 64, {}) : 128;
        samples.push(typeof val === 'number' && isFinite(val) ? val : 128);
      } catch {
        samples.push(128);
      }
    }
  } catch {
    for (let i = 0; i < waveLen; i++) samples.push(128 + Math.sin(i * 0.2) * 80);
  }

  // Draw multiple layered waveforms
  const layers = 5;
  for (let l = 0; l < layers; l++) {
    const layerHue = (baseHue + l * 40 + rng() * 30) % 360;
    const alpha = 0.15 + rng() * 0.35;
    const yOffset = height * (0.2 + l * 0.12);
    const scale = 0.4 + rng() * 0.5;
    const shift = Math.floor(rng() * waveLen);

    ctx.beginPath();
    ctx.moveTo(0, yOffset + ((samples[shift % waveLen] / 255) - 0.5) * height * scale);

    for (let x = 0; x < width; x++) {
      const idx = (shift + Math.floor((x / width) * waveLen)) % waveLen;
      const y = yOffset + ((samples[idx] / 255) - 0.5) * height * scale;
      ctx.lineTo(x, y);
    }

    ctx.strokeStyle = hslStr(layerHue, 80, 55 + rng() * 20);
    ctx.lineWidth = 1.5 + rng() * 2.5;
    ctx.globalAlpha = alpha;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // Draw glowing circles based on code hash variations
  const circles = 6 + Math.floor(rng() * 8);
  for (let c = 0; c < circles; c++) {
    const cx = rng() * width;
    const cy = rng() * height;
    const r = 20 + rng() * 80;
    const cHue = (baseHue + rng() * 360) % 360;

    const radGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    radGrad.addColorStop(0, hslStr(cHue, 90, 70) + 'cc');
    radGrad.addColorStop(0.4, hslStr(cHue, 85, 50) + '44');
    radGrad.addColorStop(1, hslStr(cHue, 80, 30) + '00');

    ctx.globalCompositeOperation = 'screen';
    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  }

  // Draw scan lines for a retro feel
  ctx.globalAlpha = 0.04;
  ctx.fillStyle = '#000';
  for (let y = 0; y < height; y += 2) {
    ctx.fillRect(0, y, width, 1);
  }
  ctx.globalAlpha = 1;

  // Subtle vignette
  const vig = ctx.createRadialGradient(
    width / 2, height / 2, height * 0.3,
    width / 2, height / 2, height * 0.8,
  );
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.6)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, width, height);

  // Draw name label at the bottom
  if (name) {
    const fontSize = Math.min(28, Math.max(14, Math.floor(width / (name.length + 2))));
    ctx.font = `bold ${fontSize}px "Inter Variable", Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    // Shadow / glow
    ctx.shadowColor = hslStr(baseHue, 90, 60);
    ctx.shadowBlur = 16;
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.fillText(name, width / 2, height - 12);
    ctx.shadowBlur = 0;
  }

  return canvas.toDataURL('image/png');
}
